import {Component, inject, OnInit} from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {PageHeaderComponent} from '../../../../../../shared/components/common/page-header/page-header.component';
import {Card} from '../../../../../../shared/components/common/card/card';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {ClientesService} from '../clientes.service';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {CreationButtons} from '../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import {DocumentoTipo, GenericCombo, Pais} from '../../../../../../shared/services/interfaces/combos.interface';
import {CombosService} from '../../../../../../shared/services/combos.service';
import {forkJoin} from 'rxjs';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { CheckboxComponent } from '@shared/components/form/checkbox/checkbox.component';
import { FileInputComponent } from '@shared/components/form/file-input/file-input.component';
import { ProspectosService } from '../../prospectos/prospectos.service';
import { documentoTipoPorDefecto } from '../documento-tipo.util';
import {
  errorFilaEstablecimiento,
  FilaEstablecimiento,
  filaEstablecimientoConDatos,
  payloadFilaEstablecimiento,
} from '../../establecimientos/establecimientos-grilla/establecimientos-grilla';
import { EstablecimientosCampos } from '../../establecimientos/establecimientos-campos/establecimientos-campos';

@Component({
  selector: 'app-crear-cliente',
  imports: [
    HasPermissionDirective,
    PageHeaderComponent,
    Card,
    ReactiveFormsModule,
    LabelComponent,
    CreationButtons,
    SelectFieldComponent,
    InputFieldComponent,
    AppFormComponent,
    // Solo los usa el bloque de contrato, comentado en el template
    // CheckboxComponent,
    // FileInputComponent,
    EstablecimientosCampos,
],
  templateUrl: './crear-cliente.html',
  styles: ``
})
export class CrearCliente implements OnInit {
  private permissionAuth = inject(AuthenticationService);

  saveForm!: FormGroup;
  loading = false;
  documentosTipos: DocumentoTipo[] = [];
  paises: Pais[] = [];
  tiposClientes: GenericCombo[] = [];

  // Alta en un solo paso: los establecimientos y el contrato viajan con el cliente
  // y el backend crea todo o nada.
  establecimientos: FilaEstablecimiento[] = [];
  mostrarErroresEstablecimientos = false;
  conContrato = new FormControl(false, {nonNullable: true});
  contratoForm!: FormGroup;

  /** Prospecto que se convierte: sus datos llegan precargados y no se vuelven a tipear. */
  prospectoId: number | null = null;

  personaTipoOptions: SelectOption[] = [
    { value: 'F', label: 'Física' },
    { value: 'J', label: 'Jurídica' },
  ];

  sexoOptions: SelectOption[] = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
  ];

  get tiposClientesOptions(): SelectOption[] {
    return this.tiposClientes.map(c => ({ value: c.id, label: c.nombre }));
  }

  get documentosTiposOptions(): SelectOption[] {
    return this.documentosTipos.map(d => ({ value: d.id, label: d.name }));
  }

  get paisesOptions(): SelectOption[] {
    return this.paises.map(p => ({ value: p.id, label: p.nombre }));
  }

  /** Establecimientos de la grilla, en el orden en que se mandan (el índice es la referencia). */
  get establecimientosCargados(): FilaEstablecimiento[] {
    return this.establecimientos.filter(filaEstablecimientoConDatos);
  }

  get establecimientosContratoOptions(): SelectOption[] {
    return this.establecimientosCargados.map((fila, i) => ({ value: i, label: fila.nombre || `Establecimiento ${i + 1}` }));
  }

  private fB = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private clientesService = inject(ClientesService);
  private combosService = inject(CombosService);
  private prospectosService = inject(ProspectosService);

  ngOnInit(): void {
    this.getCombos();
    this.makeForm();
    this.contratoForm = this.buildContratoForm();

    const prospectoId = Number(this.route.snapshot.queryParamMap.get('prospecto'));
    if (prospectoId) {
      if (!this.permissionAuth.canView('prospectos') || !this.permissionAuth.canEdit('prospectos')) {
        this.router.navigate(['/no-autorizado']);
        return;
      }
      this.prospectoId = prospectoId;
      this.cargarProspecto(prospectoId);
    }
  }

  private buildContratoForm(): FormGroup {
    return this.fB.group({
      nro_contrato: [''],
      fecha_inicio: [''],
      fecha_fin: [''],
      establecimiento: [''],
      documento: [null as File | null],
    });
  }

  /**
   * El backend toma nombre y tipo de persona del prospecto, así que esos dos quedan
   * bloqueados; documento, teléfono y mail vienen precargados pero se pueden corregir.
   */
  private cargarProspecto(id: number): void {
    this.prospectosService.show(id).subscribe({
      next: (response) => {
        const prospecto = response.data;
        const nombre = prospecto.tipo === 'J'
          ? prospecto.nombre_empresa
          : [prospecto.nombre, prospecto.apellido].filter(Boolean).join(' ');

        this.saveForm.patchValue({
          persona_tipo: prospecto.tipo,
          nombre,
          numero: prospecto.documento ?? '',
          telefono: prospecto.telefono ?? '',
          email: prospecto.email ?? '',
          crm_prospecto_id: prospecto.id,
        });
        if (prospecto.per_documento_tipo_id) {
          this.saveForm.patchValue({per_documento_tipo_id: prospecto.per_documento_tipo_id});
        }
        this.saveForm.get('persona_tipo')?.disable();
        this.saveForm.get('nombre')?.disable();
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private getCombos(): void {
    const documentosTipos = this.combosService.getDocumentoTipoCombo();
    const paises = this.combosService.getPaisesCombo();
    const tiposClientes = this.combosService.getTiposClientesCombo();

    forkJoin([documentosTipos, paises, tiposClientes]).subscribe({
      next: (response) => {
        this.documentosTipos = response[0].data;
        this.paises = response[1].data;
        this.tiposClientes = response[2].data;
        this.setDefaultPais();
        this.setDefaultDocumentoTipo();
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      persona_tipo: ['F', Validators.required],
      // En persona física `nombre` guarda el nombre completo: el alta dejó de pedir
      // el apellido por separado y siempre manda `apellido: null`.
      nombre: ['', Validators.required],
      apellido: [''],
      nombre_fantasia: [''],
      fecha_nacimiento: [''],
      fecha_constitucion: [''],
      sexo: [''],
      per_documento_tipo_id: ['', Validators.required],
      // Issue #23: la ubicación dejó de ser obligatoria. Viene precargada con Paraguay
      // por comodidad, pero se puede vaciar.
      ubi_pais_id: [''],
      numero: ['', Validators.required],
      param_tipo_cliente_id: [''],
      // Opcionales: si se completa alguno, el backend crea el dato de facturación
      // del cliente con los tres (issues #25 y #27 del 2026-09-15).
      direccion_fiscal: [''],
      telefono: [''],
      email: ['', Validators.email],
      crm_prospecto_id: null
    });
    this.updateTipoValidators();
  }

  private updateTipoValidators(): void {
    const apellidoControl = this.saveForm.get('apellido');
    const fechaNacimientoControl = this.saveForm.get('fecha_nacimiento');
    const sexoControl = this.saveForm.get('sexo');
    const nombreFantasiaControl = this.saveForm.get('nombre_fantasia');
    const fechaConstitucionControl = this.saveForm.get('fecha_constitucion');

    this.saveForm.get('persona_tipo')?.valueChanges.subscribe((value) => {
      this.setDefaultDocumentoTipo(true);

      // Si la pesonna es física el apellido es requerido, si es jurídica el nombre de fantasía es requerido
      if (value === 'F') {
        // Issue #23: el apellido dejó de ser obligatorio.
        // apellidoControl?.setValidators(Validators.required);
        apellidoControl?.clearValidators();
        fechaNacimientoControl?.clearValidators();
        sexoControl?.clearValidators();

        nombreFantasiaControl?.clearValidators();
        fechaConstitucionControl?.clearValidators();
      } else {
        apellidoControl?.clearValidators();
        fechaNacimientoControl?.clearValidators();
        sexoControl?.clearValidators();

        nombreFantasiaControl?.clearValidators();
        fechaConstitucionControl?.clearValidators();
      }

      apellidoControl?.updateValueAndValidity();
      fechaNacimientoControl?.updateValueAndValidity();
      sexoControl?.updateValueAndValidity();
      nombreFantasiaControl?.updateValueAndValidity();
      fechaConstitucionControl?.updateValueAndValidity();
    });
  }

  /** CI para persona física y RUC para jurídica; al cambiar el tipo de persona se reemplaza. */
  private setDefaultDocumentoTipo(reemplazar = false): void {
    const control = this.saveForm?.get('per_documento_tipo_id');
    if (!control || (control.value && !reemplazar)) {
      return;
    }

    const tipoId = documentoTipoPorDefecto(this.documentosTipos, this.saveForm.get('persona_tipo')?.value);
    if (tipoId) {
      control.setValue(tipoId);
    }
  }

  private setDefaultPais(): void {
    if (!this.saveForm || !this.paises.length) {
      return;
    }

    const paraguay = this.paises.find(pais => pais.nombre.toLowerCase() === 'paraguay');
    const paisControl = this.saveForm.get('ubi_pais_id');

    if (paraguay && paisControl && !paisControl.value) {
      paisControl.setValue(paraguay.id);
    }
  }

  saveCliente(): void {
    const establecimientos = this.establecimientosCargados;
    if (establecimientos.some(fila => errorFilaEstablecimiento(fila))) {
      this.mostrarErroresEstablecimientos = true;
      this.snackbarService.showSnackBar('Hay establecimientos incompletos.', 'error');
      return;
    }

    this.loading = true;
    this.clearFormToSend();

    const formData = {...this.saveForm.getRawValue()};

    // Eliminar campos según el tipo de persona
    if (formData.persona_tipo === 'J') {
      delete formData.apellido;
      delete formData.fecha_nacimiento;
      delete formData.sexo;

      if (!formData.fecha_constitucion) {
        formData.fecha_constitucion = null;
      }
    } else {
      delete formData.nombre_fantasia;
      delete formData.fecha_constitucion;

      if (!formData.fecha_nacimiento) {
        formData.fecha_nacimiento = null;
      }

      if (!formData.sexo) {
        formData.sexo = null;
      }

      // Issue #23: el apellido es opcional. Se manda null en vez de '' para no dejar
      // un apellido vacío que después ensucia el nombre_generico.
      if (!formData.apellido?.trim()) {
        formData.apellido = null;
      }
    }

    // Convertir valores vacíos de los combos a null
    for (const combo of ['param_tipo_cliente_id', 'ubi_pais_id'] as const) {
      if (formData[combo] === '') {
        formData[combo] = null;
      }
    }

    // El dato de facturación se crea si viene alguno de los tres; los vacíos no se mandan
    for (const campo of ['direccion_fiscal', 'telefono', 'email'] as const) {
      if (!formData[campo]?.trim()) {
        delete formData[campo];
      }
    }

    const payload = this.toFormData(formData);
    establecimientos.forEach((fila, i) => this.appendCampos(payload, `establecimientos[${i}]`, payloadFilaEstablecimiento(fila)));

    if (this.conContrato.value) {
      const contrato = {...this.contratoForm.value};
      // Con un solo establecimiento no hace falta elegirlo
      if (contrato.establecimiento === '' && establecimientos.length === 1) {
        contrato.establecimiento = 0;
      }
      const documento: File | null = contrato.documento;
      delete contrato.documento;
      this.appendCampos(payload, 'contrato', contrato);
      if (documento) {
        payload.append('contrato[documento]', documento, documento.name);
      }
    }

    this.clientesService.store(payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Cliente creado con correctamente');
          this.router.navigate(['../', response.data.id], {relativeTo: this.route});
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  private toFormData(datos: Record<string, unknown>): FormData {
    const payload = new FormData();
    this.appendCampos(payload, '', datos);
    return payload;
  }

  /** Agrega los campos con valor; los vacíos no se mandan (el backend los toma como null). */
  private appendCampos(payload: FormData, prefijo: string, datos: Record<string, unknown>): void {
    for (const [campo, valor] of Object.entries(datos)) {
      if (valor === null || valor === undefined || valor === '') continue;
      payload.append(prefijo ? `${prefijo}[${campo}]` : campo, String(valor));
    }
  }

  private clearFormToSend(): void {
    if (this.saveForm.get('persona_tipo')?.value === 'F') {
      this.saveForm.get('nombre_fantasia')?.reset();
      this.saveForm.get('fecha_constitucion')?.reset();
    } else {
      this.saveForm.get('apellido')?.reset();
      this.saveForm.get('fecha_nacimiento')?.reset();
      this.saveForm.get('sexo')?.reset();
    }
  }

  goBack(): void {
    if (this.prospectoId) {
      this.router.navigate(['/crm/prospectos', this.prospectoId]);
      return;
    }
    this.router.navigate(['../'], {relativeTo: this.route});
  }
}
